<template>
  <div id="app" ref="app" :class="getAppClasses">
    <Header v-if="isHeaderActive"></Header>
    <BarAudio v-if="isBroadcastActive"></BarAudio>
    <main>
      <div v-if="isNotFound" class="notFound">
        <div class="box dashed a-accent">
          <span class="underline">404 Not Found</span> <br /><br />If you’re
          looking for a past broadcast, we’re still working on migrating our
          archives to the new site! In the meantime
          <router-link to="/">Tune In</router-link> to the live stream or
          <router-link to="/archive">Browse</router-link> the archives.
        </div>
      </div>
      <router-view v-else></router-view>
    </main>
    <Footer v-if="isFooterActive" :inverted="isInverted"></Footer>
    <PreviewBanner />
    <vue-progress-bar></vue-progress-bar>
  </div>
</template>

<script>
import "vue-event-calendar/dist/style.css";
import "ress";

import delve from "dlv";
import scrollDir from "bloody-scroll-direction";
import touchable from "haz-touch";
import { mixin } from "./lib/content-state";
import newTabify from "./lib/new-tabify";
import BarAudio from "./components/BarAudio";
import PreviewBanner from "./components/PreviewBanner";
import Header from "./components/Header";
import Footer from "./components/Footer";

export default {
  name: "Layout",
  components: { Header, Footer, BarAudio, PreviewBanner },
  mixins: [mixin],
  data() {
    return {
      frame: {},
      scrollingUp: false,
    };
  },
  metaInfo() {
    let meta = this.site ? Object.assign({}, this.site.meta) : {};

    if (this.page && this.page.meta) {
      meta = Object.assign(meta, this.page.meta);
    }

    let title = delve(this.page, "title") || delve(this.site, "title");

    let metaTags = [
      { charset: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        name: "twitter:card",
        content: "summary_large_image",
        vmid: "og:card",
      },
      {
        name: "twitter:image",
        content: meta.image,
        vmid: "og:image",
      },
      {
        name: "description",
        content: meta.description,
        vmid: "description",
      },
      {
        property: "og:title",
        content: title,
        vmid: "og:title",
      },
      {
        property: "og:description",
        content: meta.description,
        vmid: "og:description",
      },
      {
        property: "og:type",
        content: "website",
        vmid: "og:type",
      },
      {
        property: "og:image",
        content: meta.image,
        vmid: "og:image",
      },
    ];

    if (this.isNotFound) {
      metaTags.push({
        property: "prerender-status-code",
        content: 404,
        vmid: "prerender-status-code",
      });
    }

    return {
      title: this.page ? this.page.title : false,
      titleTemplate: (titleChunk) => {
        return titleChunk ? `${titleChunk} - dublab` : "dublab";
      },
      meta: metaTags,
    };
  },
  created() {
    this.$store.dispatch("fetchPage", "/star");
    this.$store.dispatch("refreshBroadcast");
    this.$store.dispatch("startTime");

    setInterval(() => this.$store.dispatch("refreshBroadcast"), 1000 * 60 * 60);

    this.frame.scrollDir = scrollDir.create();
    this.frame.scrollDir.on("change", (data) => {
      this.scrollingUp = data.direction === -1;
    });

    newTabify();

    this.$Progress.start();
    this.$router.beforeEach((to, from, next) => {
      if (to.meta.progress !== undefined) {
        let meta = to.meta.progress;
        this.$Progress.parseMeta(meta);
      }
      this.$Progress.start();
      next();
    });

    this.$router.afterEach(() => {
      this.$Progress.finish();
    });
  },
  mounted() {
    // const urlParams = new URLSearchParams(window.location.search);
    // const ignoreCache = urlParams.get('ignore_cache');
    // if (ignoreCache) {
    //   sessionStorage.ignoreCache = true;
    // }

    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  willDestroy() {
    this.frame.scrollDir.destroy();
    window.removeEventListener("resize", this.handleResize);
    this.$store.dispatch("stopTime");
  },
  computed: {
    isHeaderActive() {
      return this.$route.name !== null;
    },
    isHeaderAtBottom() {
      return false;
    },
    isFooterActive() {
      return !['/', '/tv'].includes(this.$route.path);
    },
    isBroadcastActive() {
      return this.$store.state.audio.broadcast.active;
    },
    isLoading() {
      return this.$store.state.ui.loading;
    },
    isNotFound() {
      //don't include schedule page which uses google calendar api
      return this.page._notfound && !this.page.url.includes("schedule");
    },
    isHeaderSticky() {
      return true && this.$route.path !== "/";
    },
    isInverted() {
      return this.$route.name === "drive" || this.$route.name === "takeover";
    },
    getAppClasses() {
      return [
        { loading: this.isLoading },
        { bottom: this.isHeaderAtBottom },
        { audioVisible: this.isBroadcastActive },
        { sticky: this.isHeaderSticky },
        { "no-touch": !touchable },
        {
          drive:
            this.$route.name === "drive" || this.$route.name === "takeover",
        },
        { invert: this.isInverted },
      ];
    },
  },
  methods: {
    handleResize: function () {
      if (this.$refs.app) {
        // set min height of app to real innerHeight so works on mobile
        this.$refs.app.style.minHeight = window.innerHeight + "px";
      }
    },
  },
};
</script>

<style scoped>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

#app>>>.Header {
  width: 100%;
  position: relative;
  z-index: 10;
}

@media (min-width: 920px) {
  #app.sticky>>>.HeaderWrap {
    position: sticky;
    top: 0;
    z-index: 10;
  }
}

#app.bottom>>>.Header {
  position: fixed;
  bottom: 0;
  left: 0;
  top: auto;
  margin-bottom: 0;
}

.BarAudio {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 11;
}

#app.bottom.audioVisible .BarAudio {
  bottom: 4.1rem;
}

#app.audioVisible>>>.Footer {
  padding-bottom: 5rem;
}

main {
  display: flex;
  flex: 1;
}

@keyframes slide-down {
  from {
    transform: translateY(-100%);
  }

  to {
    transform: translateY(0);
  }
}

@media (max-width: 920px) {
  #app.bottom>>>.Header {
    position: fixed;
    bottom: auto;
    left: 0;
    top: 0;
  }

  #app.bottom.audioVisible .BarAudio {
    bottom: 0;
  }

  #app>>>.Header.mobile {}

  #app.audioVisible>>>.Footer {
    padding-bottom: 6.5rem;
  }
}

.notFound {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}

.notFound .box {
  max-width: 100%;
  width: 62rem;
}

:root {
  --y: #ffd200;
  --y2: #fff2b3;
  --b: #89b9e3;
  --b2: #dceaf7;
  --r: #f05582;
  --r2: #fbccda;
  --g: #94c649;
  --g2: #dfeec8;
}

#app.drive {
  background: #000;
  /*background: var(--accent);*/
  /*background-color: #282828;*/
  /*background: linear-gradient(#ffd200, #f9e2ad, #94c649, #d3e4b5, #89b9e3, #c1d7ed, #f05582);
  background: linear-gradient(#89b9e3, #c1d7ed, #f05582, #eda6b9, #ffd200, #f6de98, #94c649);
  background: linear-gradient(#89b9e3, #c1d7ed);*/

  /*background: linear-gradient(
    0deg,
    #ffed99,
    #ffd200,
    #cae3a4,
    #94c649,
    #c4dcf1,
    #89b9e3,
    #f8aac1,
    #f05582,
    #f8aac1
  );*/
}

#app.drive>>>.Header {
  margin-bottom: 0;
}
</style>

<style>
:root {
  /**
   * Padding
   *
   * Typically you will just set rem values directly,
   * but these shortcuts are provided when you need
   * perfect equal padding (when using bordered boxes)
   */
  --p1: 0.6rem 1rem 0.8rem 1rem;
  --py1: 0.6rem 0 0.8rem 0;
  --py1bar: 0.6rem 0 1rem 0;
  --p2: 1.4rem 2rem 1.6rem 2rem;
  --py2: 1.4rem 0 1.6rem 0;
  /* Fonts Families */
  --ffdub: AdieuLight, Helvetica, Arial, sans-serif;
  --ffsans: SuisseIntl, Helvetica, Arial, sans-serif;
  --ffsansmono: SwissMono, Helvetica, Arial, sans-serif;
  --ffneue: SuisseNeue, Helvetica, Arial, sans-serif;
  /* Font Sizes */
  --fs0: 1.2rem;
  --fs1: 1.45rem;
  --fs2: 2rem;
  --fs3: 3.6rem;
  /* Line Heights */
  --lh1: 1;
  --lh2: 1.1;
  --lh3: 1.2;
  --lh4: 1.3;
  /* Fonts */
  --large: normal var(--fs3) / var(--lh2) var(--ffdub);
  --medium: normal var(--fs2) / var(--lh3) var(--ffdub);
  --mediumsmall: normal 1.2rem / 1 var(--ffdub);
  --smallish: normal var(--fs1) / var(--lh4) var(--ffneue);
  --small: normal var(--fs1) / var(--lh4) var(--ffsans);
  --small_mono: normal var(--fs0) / var(--lh4) var(--ffsansmono);
  --medium_mono: normal var(--fs2) / var(--lh3) var(--ffsansmono);
  /* Colors */
  --accent: #0287fe;
  --grey: #999;
  --red: #f00;
  /* Borders */
  --b1: 1px solid black;
  --b1d: 1px dashed black;
  /* Border Radius */
  --br1: 2rem;
  --br2: 3rem;
  /* Columns */
  --c0-5: calc((1 / 24) * 100%);
  --c1: calc((1 / 12) * 100%);
  --c2: calc((2 / 12) * 100%);
  --c3: calc((3 / 12) * 100%);
  --c4: calc((4 / 12) * 100%);
  --c5: calc((5 / 12) * 100%);
  --c6: calc((6 / 12) * 100%);
  --c7: calc((7 / 12) * 100%);
  --c8: calc((8 / 12) * 100%);
  --c9: calc((9 / 12) * 100%);
  --c10: calc((10 / 12) * 100%);
  --c11: calc((11 / 12) * 100%);
  --c12: 100%;
}

/* Body */

html {
  font-size: 62.5%;
}

@media (max-width: 700px) {
  html {
    /*font-size: 53.5%;*/
    font-size: 57.5%;
  }
}

body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /*min-height: 100vh;*/
  font: var(--medium);

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body>main {
  display: flex;
  flex-direction: column;
  flex: 1;
}

@media (max-width: 1000px) {
  :root {
    --fs3: 2.6rem;
    --large: normal var(--fs3) / var(--lh3) var(--ffdub);
  }

  html {
    /*font-size: 50%;*/
  }
}

/*main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}*/

/* UI Reset */

b,
strong {
  font-weight: normal;
}

h1,
h2,
h3,
h4,
h5,
h6,
h7 {
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
}

button,
input {
  outline: none;
  letter-spacing: inherit;
  text-align: inherit;
}

ul,
ol,
li {
  list-style: none;
}

a {
  color: inherit;
  text-decoration: inherit;
}

img,
video {
  width: 100%;
  height: auto;
}

input::placeholder {
  color: black;
}

input[type="text"]::-moz-placeholder {
  color: black;
  opacity: 1;
}

input[type="text"]:-moz-placeholder {
  color: black;
  opacity: 1;
}

::selection {
  background: var(--accent);
}

summary {
  outline: none;
  cursor: pointer;
}

/* Grid */

.grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 2rem;
  padding: 2rem;
  width: 100%;
}

.grid-auto-lg {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50rem, 1fr));
  grid-gap: 4rem 2rem;
  grid-column: 1 / -1;
}

.grid-auto-md {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(38rem, 1fr));
  grid-gap: 4rem 2rem;
  grid-column: 1 / -1;
}

.grid-auto-sm {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(32rem, 1fr));
  grid-gap: 4rem 2rem;
  grid-column: 1 / -1;
}

@media (max-width: 55rem) {

  .grid-auto-lg,
  .grid-auto-md,
  .grid-auto-sm {
    grid-template-columns: 1fr;
  }
}

/* Loading */

.loading {
  cursor: wait !important;
}

.loading * {
  pointer-events: none;
}

/* Type */

@font-face {
  font-family: "AdieuLight";
  src: url("assets/fonts/Adieu-Light.woff2") format("woff2"),
    url("assets/fonts/Adieu-Light.woff") format("woff");
}

@font-face {
  font-family: "Adieu";
  src: url("assets/fonts/Adieu-Regular.woff2") format("woff2"),
    url("assets/fonts/Adieu-Regular.woff") format("woff");
  font-weight: bold;
}

@font-face {
  font-family: "Minion";
  src: url("assets/fonts/MinionPro-Regular.woff2") format("woff2"),
    url("assets/fonts/MinionPro-Regular.woff") format("woff");
}

@font-face {
  font-family: "SuisseIntl";
  src: url("assets/fonts/SuisseIntl.woff2") format("woff2"),
    url("assets/fonts/SuisseIntl.woff") format("woff");
}

@font-face {
  font-family: "SuisseIntl";
  src: url("assets/fonts/SuisseIntl-Italic.woff2") format("woff2"),
    url("assets/fonts/SuisseIntl-Italic.woff") format("woff");
  font-style: italic;
}

@font-face {
  font-family: "SwissMono";
  src: url("assets/fonts/SwissMono.woff2") format("woff2"),
    url("assets/fonts/SwissMono.woff") format("woff");
}

@font-face {
  font-family: "SwissMono";
  src: url("assets/fonts/SuisseIntlMono-Bold.woff2") format("woff2"),
    url("assets/fonts/SuisseIntlMono-Bold.woff") format("woff");
  font-weight: bold;
}

@font-face {
  font-family: "SuisseNeue";
  src: url("assets/fonts/SuisseNeue-Regular.woff2") format("woff2"),
    url("assets/fonts/SuisseNeue-Regular.woff") format("woff");
}

@font-face {
  font-family: "YoppaRegular";
  src: url("assets/fonts/Yoppa-Regular.woff") format("woff"),
    url("assets/fonts/Yoppa-Regular.otf") format("otf");
}

@font-face {
  font-family: "FormaDJR";
  src: url("assets/fonts/FormaDJRText-Regular.woff") format("woff");
}

@font-face {
  font-family: "MicroGBE";
  src: url("assets/fonts/MICROGBE.woff") format("woff");
}

@font-face {
  font-family: "CCBiffBamBoom";
  src: url("assets/fonts/CCBiffBamBoom.woff") format("woff");
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Monstera_Bold';
  src: url('assets/fonts/Monstera-Bold-web.eot');
  src: url('assets/fonts/Monstera-Bold-web.eot?#iefix') format('embedded-opentype'),
    url('assets/fonts/Monstera-Bold-web.woff') format('woff'),
    url('assets/fonts/Monstera-Bold-web.woff2') format('woff2'),
    url('assets/fonts/Monstera-Bold-web.ttf') format('truetype'),
    url('assets/fonts/Monstera-Bold-web.svg#svgFontName') format('svg');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Monstera_Light';
  src: url('assets/fonts/Monstera-Light-web.eot');
  src: url('assets/fonts/Monstera-Light-web.eot?#iefix') format('embedded-opentype'),
    url('assets/fonts/Monstera-Light-web.woff') format('woff'),
    url('assets/fonts/Monstera-Light-web.woff2') format('woff2'),
    url('assets/fonts/Monstera-Light-web.ttf') format('truetype'),
    url('assets/fonts/Monstera-Light-web.svg#svgFontName') format('svg');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Monstera';
  src: url('assets/fonts/Monstera-Regular-web.eot');
  src: url('assets/fonts/Monstera-Regular-web.eot?#iefix') format('embedded-opentype'),
    url('assets/fonts/Monstera-Regular-web.woff') format('woff'),
    url('assets/fonts/Monstera-Regular-web.woff2') format('woff2'),
    url('assets/fonts/Monstera-Regular-web.ttf') format('truetype'),
    url('assets/fonts/Monstera-Regular-web.svg#svgFontName') format('svg');
  font-weight: normal;
  font-style: normal;
}

/* Hovers */

.no-touch:not(.invert) .hover:hover,
.no-touch:not(.invert) a:hover .a-hover,
.no-touch:not(.invert) .a-hover a:hover {
  color: var(--accent);
  border-color: var(--accent) !important;
}

/* Blazy */

.b-lazy {
  opacity: 0;
}

.b-lazy.b-loaded {
  opacity: 1;
}

/* Copy */

.copy {
  hyphens: auto;
}

.copy.unhyphen {
  hyphens: none;
}

.copy>*+*,
.owl>*+* {
  margin-top: 1em;
}

/*.copy > h3 + p {
  margin-top: 0;
}*/

.copy a:not(.pill) {
  color: var(--accent);
}

.copy h2 a:not(.pill),
.copy h3 a:not(.pill) {
  color: #000;
  text-decoration: none;
}

.no-touch .copy a:not(.pill):hover {
  color: var(--accent);
  border-color: var(--accent);
}

.no-touch .copy h2 a:not(.pill):hover,
.no-touch .copy h3 a:not(.pill):hover {
  color: #000;
  border-color: #000;
}

.copy ul {
  padding-top: 2rem;
}

.copy ul li {
  list-style: none;
  padding-left: 2em;
  position: relative;
}

.copy ul li:before {
  content: "";
  display: block;
  position: absolute;
  left: 0;
  width: 1.5rem;
  height: 1.5rem;
  top: 0.5rem;
  background-image: url("assets/images/asterisk.svg");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.copy ol {
  counter-reset: numlist;
}

.copy ol li {
  list-style: none;
  padding-left: 2em;
  position: relative;
}

.copy ol li:before {
  counter-increment: numlist;
  content: counter(numlist) ". ";
  display: block;
  position: absolute;
  left: 0;
}

.copy ol li:nth-child(n + 10) {
  padding-left: 2.5em;
}

.copy li+li {
  margin-top: 1em;
}

.copy h1 {
  font: var(--large);
  text-align: center;
}

.copy h2 {
  text-align: center;
}

.copy h2 a,
.copy h3 a {
  border-bottom: 1px solid black;
}

.copy h2:not(:first-child),
.copy h3:not(:first-child) {
  margin-top: 4rem;
}

.copy h5 {
  font: var(--small);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.copy .wp-caption {
  max-width: 100%;
}

.copy figcaption,
.copy .wp-caption-text {
  font: var(--small);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
  margin-top: 1rem;
}

.copy blockquote {
  padding-left: 2em;
}

.copy .accordion {
  padding-left: 2em;
  position: relative;
}

.copy .accordion div {
  font: var(--small);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-top: 2rem;
}

.copy .accordion label {
  position: relative;
  user-select: none;
}

.copy

/* reserve space for clicking */
.copy .accordion label::before {
  content: "";
  display: block;
  position: absolute;
  width: 2em;
  height: 1em;
  left: -2em;
  top: 0;
}

.copy .accordion label::after {
  content: "";
  display: block;
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  left: -2em;
  top: 0.5rem;
  background-image: url("assets/images/ui_accordion.svg");
  background-size: 3rem 1.5rem;
  background-repeat: no-repeat;
  background-position: top left;
}

.copy .accordion input[type="checkbox"]:checked~label::after {
  background-position: top right;
}

.copy .logos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 1rem 0;
}

.copy .tracklist {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  font-family: var(--ffsansmono);
  font-size: var(--fs1);
  flex-direction: column;
  padding: 1rem 0;
}

.copy .tracklistHeading {}

.copy .logos>a,
.copy .logos>span {
  display: inline-block;
  padding: 1rem 0.5rem;
  width: 33%;
}

.copy .logos br {
  display: none !important;
}

.copy .callout {
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  padding-top: 2rem;
  padding-bottom: 2rem;
  margin-top: 3rem;
  text-align: center;
}

.copy .block {
  break-inside: avoid;
}

.copy .columns {
  columns: 2;
}

.copy h1,
.copy h2,
.copy h3,
.copy h4 {
  hyphens: none;
}

/*.copy a.big-donate {
  display: block;
  border: var(--b1d);
  border-radius: var(--br1);
  font: var(--large);
  text-align: center;
  padding: var(--p2);
  border-color: var(--accent);
  color: var(--accent);
  width: 100%;
}

.no-touch .copy a.big-donate:hover {
  color: #000;
}*/

.copy .DublabGallery {
  width: 80%;
  margin: 0 auto;
}

.copy .DublabGallery:not(:first-child) {
  margin-top: 2em;
}

/**
 * Icons
 */

[class*="icon-"] {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 1em;
}

[class*="icon-"]::after {
  content: "";
  display: block;
  padding-top: 100%;
}

[class*="icon-"].disabled,
[class*="icon-"][disabled] {
  opacity: 0.25;
  pointer-events: none;
  user-select: none;
}

[class*="icon-"].large {
  border: var(--b1);
  overflow: hidden;
  border-radius: 22%;
  /*height: 8rem;
  width: 8rem;*/
  width: 100%;
  height: 100%;
  background-size: 30%;
}

[class*="-sprite"] {
  background-size: 200% auto;
  background-position: left 50%;
}

[class*="-sprite"].active,
[class*="-sprite"].router-link-active {
  background-position: right 50%;
}

.icon-player-play.large {
  background-position: 55% 50%;
}

.icon-player-play.large-square {
  background-position: 55% 50%;
}

.icon-button-arrow {
  background-image: url("assets/images/ui_button-arrow.svg");
}

.icon-button-close {
  background-image: url("assets/images/ui_button-close.svg");
}

.icon-button-plus {
  background-image: url("assets/images/ui_button-plus.svg");
}

.icon-button-pause {
  background-image: url("assets/images/ui_button-pause.svg");
}

.icon-button-play {
  background-image: url("assets/images/ui_button-play.svg");
}

.icon-button-toggle {
  background-image: url("assets/images/ui_button-toggle.svg");
}

.icon-player-pause {
  background-image: url("assets/images/ui_player-pause.svg");
}

.icon-player-play {
  background-image: url("assets/images/ui_player-play.svg");
}

.icon-player-stop {
  background-image: url("assets/images/ui_player-stop.svg");
}

.icon-search {
  background-image: url("assets/images/ui_search.svg");
}

.icon-search-big {
  background-image: url("assets/images/ui_search-big.svg");
}

.icon-search-sprite {
  background-image: url("assets/images/ui_search-sprite.svg");
}

.icon-search-big-sprite {
  background-image: url("assets/images/ui_search-big-sprite.svg");
}

.icon-mobile-menu {
  background-image: url("assets/images/svg_mobile-menu.svg");
}

.icon-mobile-menu-close {
  background-image: url("assets/images/svg_mobile-menu-close.svg");
}

.icon-button-arrow.left {
  transform: rotate(90deg);
}

.icon-button-arrow.right {
  transform: rotate(270deg);
}

.icon-button-arrow.up {
  transform: rotate(180deg);
}

[class*="inline-icon-"] {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 1.86rem;
  height: 1.4rem;
  display: inline-block;
}

.inline-icon--flag-de {
  background-image: url("assets/images/flag_de.svg");
}

.inline-icon--flag-jp {
  background-image: url("assets/images/flag_jp.svg");
}

.inline-icon--flag-es {
  background-image: url("assets/images/flag_es.svg");
}

.inline-icon--flag-br {
  background-image: url("assets/images/flag_br.svg");
}

/**
 * Button
 */

.button {
  display: inline-block;
  cursor: pointer;
  user-select: none;
  -moz-user-select: none;
}

/**
 * Pill
 */

.pill {
  position: relative;
  display: inline-block;
  padding: 0.6rem 2rem 0.8rem 2rem;

  background-color: white;
  color: black;
  border: var(--b1d);
  border-radius: var(--br1);

  font: var(--medium);
  text-align: center;
}

.pill.solid {
  border-style: solid;
}

.pill.active {
  background-color: var(--accent);
  color: white;
  border-style: solid;
}

.no-touch .pill-hover .pill:not(.active):hover,
.no-touch .pill-hover input.pill:hover,
.no-touch input.pill:focus {
  border-color: var(--accent);
}

.pill.disabled,
.pill[disabled] {
  pointer-events: none;
  opacity: 0.25;
  user-select: none;
}

.pill.external {
  background-image: url(assets/images/ui_button-arrow-angle.svg);
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: auto 57.5%;
}

.pill.toggle {
  background-image: url(assets/images/ui_button-toggle.svg);
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: auto 57.5%;
  padding-right: 4.5rem;
}

.pill.toggle.active {
  background-color: black;
  background-image: url("assets/images/ui_button-close.svg");
}

.pill.circle {
  padding-left: 0;
  padding-right: 0;
  width: 4rem;
}

.donatepill {
  position: relative;
  margin-left: 2rem;
}

/*.pill.circle span {
  padding: 0;
  height: 4rem;
  width: 3.9rem;
  line-height: 3.8rem;
  text-align: center;
}*/

select.pill {
  appearance: none;
  background-image: url("assets/images/ui_button-arrow.svg");
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: auto 57.5%;
  padding-right: 4.5rem;
  cursor: pointer;
  outline: none;
}

select.pill.active {
  /*background-image: url('assets/images/ui_button-close.svg');*/
}

@-moz-document url-prefix() {
  select.pill option {
    background-color: white;
  }
}

select.pill:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 #000;
}

select.pill option:not(:checked) {
  color: black;
  /* prevent <option>s from becoming transparent as well */
}

.pill.big {
  padding: 1.7rem 3rem 2.1rem 3rem;
  border-radius: 8rem;
  font: var(--large);
}

.pill.glow {
  animation: glow 2s linear infinite alternate;
  border-width: 0;
}

@keyframes glow {
  from {
    box-shadow: 0px 0px 1rem 0.5rem var(--accent);
  }

  to {
    box-shadow: 0px 0px 1rem 0 var(--accent);
  }
}

/**
 * Radio
 */

.radio-group {
  display: flex;
  width: 100%;
}

.radio-group>* {
  flex: 1;
}

.radio-pill input[type="radio"] {
  display: none;
}

.radio-pill label {
  display: block;
  width: 100%;

  border-top: 1px dashed black;
  border-bottom: 1px dashed black;
  border-right: 1px dashed black;
  padding: 0.6rem 2rem 0.8rem 2rem;

  background-color: white;
  border: var(--b1d);
  border-left: 0;

  color: #000;
  text-align: center;
  cursor: pointer;
}

.no-touch .radio-pill label:hover {
  border-color: var(--accent);
}

.radio-pill:first-child label {
  border-left: 1px dashed black;
  border-radius: var(--br1) 0 0 var(--br1);
}

.radio-pill:last-child label {
  border-radius: 0 var(--br1) var(--br1) 0;
}

.radio-pill-vertical.radio-pill label {
  border-radius: var(--br1);
  border-left: 1px dashed black;
}

.radio-pill-vertical.radio-pill:not(:first-child) label {
  margin-top: 2rem;
}

.radio-pill input[type="radio"]:checked+label {
  background: var(--accent);
  border-style: solid;
  color: #fff;
  border-color: #000;
}

.invert .radio-group input[type="radio"]:checked+label {
  background: #fff;
  color: var(--accent);
}

/**
 * Lists
 */

.bullet-list>* {
  display: inline;
}

.bullet-list>*:not(:last-child)::after {
  content: ", ";
}

.bullet-list.tags {
  font: var(--small_mono);
  letter-spacing: 0.03em;
}

.comma:not(:last-child):after {
  content: ", ";
}

/**
 * Accordion
 */

.accordion>label {
  cursor: pointer;
}

.accordion>input[type="checkbox"],
.accordion>div {
  display: none;
}

.accordion>input[type="checkbox"]:checked~div {
  display: block;
}

/**
 * Boxes
 */

.box {
  border-radius: var(--br1);
  padding: var(--p2);
  text-align: center;
}

.box.dashed {
  border: var(--b1d);
}

.box.solid {
  border: var(--b1);
}

.no-touch a.box:hover {
  border-color: var(--accent);
}

/**
 * Typography
 */

.underline {
  /*text-decoration: underline;*/
  border-bottom: 1px solid black;
}

.invert .underline {
  border-color: white;
}

.a-grey a,
.grey {
  color: var(--grey);
}

.a-accent a,
.accent {
  color: var(--accent);
}

.a-red a,
.red {
  color: var(--red);
}

.a-underline a {
  border-bottom: 1px solid currentColor;
}

/**
 * Utils
 */
.tal {
  text-align: left;
}

.db {
  display: block;
}

/* Dev */
.dev {
  outline: 1px solid #912eff;
}

.dev>* {
  outline: 1px solid #5497ff;
}

.dev>*>* {
  outline: 1px solid #51feff;
}

.dev>*>*>* {
  outline: 1px solid #ff0000;
}

.dev>*>*>* * {
  outline: 1px solid #00ff00;
}
</style>
